import { useState, useEffect } from 'react';
import { db, storage } from '../../../firebase.config';
import { collection, addDoc, updateDoc, doc, deleteDoc, getDocs } from 'firebase/firestore';
import { ref, uploadBytesResumable, getDownloadURL, deleteObject, uploadBytes } from 'firebase/storage'; 
import '../../../styles/manageProducts.css';

const ManageRetinaData = () => {
    const [retinaImages, setRetinaImages] = useState([]);
    const [showModal, setShowModal] = useState(false);
    const [retinaData, setRetinaData] = useState({
        name: '',
        details: '',
        imgUrl: [],
        instagram: '',
    });
    const [editImageId, setEditImageId] = useState(null);
    const [loading, setLoading] = useState(true);
    const [selectedImages, setSelectedImages] = useState([]); // Added this line
    const [uploading, setUploading] = useState(false);
    const [contextMenu, setContextMenu] = useState({
        visible: false,
        x: 0,
        y: 0,
        index: null,
    });

    useEffect(() => {
        const handleClickOutside = () => {
          if (contextMenu.visible) setContextMenu({ ...contextMenu, visible: false });
        };
        document.addEventListener("click", handleClickOutside);
        return () => document.removeEventListener("click", handleClickOutside);
    }, [contextMenu]);
    
    // Fetch all retina images
    const fetchRetinaImages = async () => {
        const retinaRef = collection(db, "retinaImages");
        const querySnapshot = await getDocs(retinaRef);
        
        const imagesList = querySnapshot.docs.map(doc => {
            const data = doc.data();
            return {
                id: doc.id,
                ...data,
            };
        });

        console.log("Fetched retina images:", imagesList);
        setRetinaImages(imagesList);
        setLoading(false);
    };

    // Add retina image
    const addRetinaImage = async () => {
        try {
            setLoading(true);
    
            // Upload images
            const uploadedImageUrls = [];
            for (const file of selectedImages) {
                const storageRef = ref(storage, `retina-images/${retinaData.name}/${file.name}`);
                const uploadTask = uploadBytesResumable(storageRef, file);
                await new Promise((resolve, reject) => {
                    uploadTask.on(
                        "state_changed",
                        null,
                        (error) => reject(error),
                        async () => {
                            const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
                            uploadedImageUrls.push(downloadURL);
                            resolve();
                        }
                    );
                });
            }
    
            // Prepare final retina image object
            const newRetinaData = {
                ...retinaData,
                imgUrl: uploadedImageUrls,
            };
    
            const retinaRef = collection(db, "retinaImages");
            await addDoc(retinaRef, newRetinaData);
    
            setLoading(false);
            setShowModal(false);
            setSelectedImages([]); // Clear selected images
            setRetinaData({
                name: '',
                details: '',
                imgUrl: [],
                instagram: '',
            });
            fetchRetinaImages();
        } catch (error) {
            console.error("Error adding retina image:", error);
            setLoading(false);
        }
    };
    
    const handleImageUpload = async (e) => {
        const files = Array.from(e.target.files);
        setUploading(true);
    
        try {
            const uploadPromises = files.map(file => {
                const storageRef = ref(storage, `retina-images/${file.name}-${Date.now()}`);
                return uploadBytes(storageRef, file).then(snapshot => getDownloadURL(snapshot.ref));
            });
    
            const urls = await Promise.all(uploadPromises);
    
            setRetinaData(prev => {
                const existing = Array.isArray(prev.imgUrl) ? prev.imgUrl : [];
                return {
                    ...prev,
                    imgUrl: [...existing, ...urls],
                };
            });
        } catch (error) {
            console.error("Error uploading images:", error);
        } finally {
            setUploading(false);
        }
    };
    
    const setImageAsMain = (index) => {
        setRetinaData(prev => {
            if (!prev || !Array.isArray(prev.imgUrl)) return prev;

            const images = [...prev.imgUrl];
            if (!images[index]) return prev;

            const [selected] = images.splice(index, 1);
            images.unshift(selected);

            return { ...prev, imgUrl: images };
        });
    };

    // Edit retina image
    const editRetinaImage = async () => {
        try {
            setLoading(true);
            const imageRef = doc(db, "retinaImages", editImageId);
    
            // Upload new images if any were added
            const uploadedImageUrls = [...retinaData.imgUrl]; // Start with existing images
    
            // If there are new files to upload, handle them
            const filesToUpload = Array.isArray(selectedImages) ? selectedImages : [];
            for (const file of filesToUpload) {
                if (file instanceof File) {
                    const storageRef = ref(storage, `retina-images/${retinaData.name}/${file.name}`);
                    const uploadTask = uploadBytesResumable(storageRef, file);
                    await new Promise((resolve, reject) => {
                        uploadTask.on(
                            "state_changed",
                            null,
                            (error) => reject(error),
                            async () => {
                                const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
                                uploadedImageUrls.push(downloadURL);
                                resolve();
                            }
                        );
                    });
                }
            }
    
            // Build updated retina image object
            const updatedData = {
                ...retinaData,
                imgUrl: uploadedImageUrls,
            };
    
            // Update Firestore document
            await updateDoc(imageRef, updatedData);
    
            setLoading(false);
            setShowModal(false);
            setSelectedImages([]); // Clear selected images
            fetchRetinaImages();
        } catch (error) {
            console.error("Error editing retina image:", error);
            setLoading(false);
        }
    };

    const handleMainImageDelete = async (index) => {
        const imageToDelete = retinaData.imgUrl[index];
    
        if (window.confirm("Are you sure you want to delete this image?")) {
            try {
                // If it's a URL (already uploaded)
                if (typeof imageToDelete === 'string' && imageToDelete.startsWith('https://')) {
                    const path = imageToDelete.split('/o/')[1].split('?')[0];
                    const decodedPath = decodeURIComponent(path);
                    const imageRef = ref(storage, decodedPath);
                    await deleteObject(imageRef);
                }
    
                const updatedImages = retinaData.imgUrl.filter((_, i) => i !== index);
                setRetinaData(prev => ({ ...prev, imgUrl: updatedImages }));
            } catch (err) {
                console.error("Error deleting image:", err);
                alert("Failed to delete image.");
            }
        }
    };
    
    // Delete retina image
    const deleteRetinaImage = async (id) => {
        const confirmation = window.confirm("Are you sure you want to delete this retina image?");
        if (confirmation) {
            try {
                const imageRef = doc(db, "retinaImages", id);
                await deleteDoc(imageRef);
                fetchRetinaImages();
            } catch (error) {
                console.error("Error deleting retina image:", error);
            }
        }
    };

    // Handle modal open for adding or editing
    const openModal = (image = null) => {
        if (image) {
            setRetinaData(image);
            setEditImageId(image.id);
        } else {
            setRetinaData({
                name: '',
                details: '',
                imgUrl: [],
                instagram: '',
            });
            setEditImageId(null);
        }
        setSelectedImages([]); // Clear selected images when opening modal
        setShowModal(true);
    };

    // Handle file selection
    const handleFileSelect = (e) => {
        const files = Array.from(e.target.files);
        setSelectedImages(files);
        // Also upload immediately
        handleImageUpload(e);
    };

    // Fetch retina images when component mounts
    useEffect(() => {
        fetchRetinaImages();
    }, []);

    if (loading) return <p>Loading retina images...</p>;

    return (
        <div className="manage-products">
            <h1>Manage Retina Images</h1>
            <button onClick={() => openModal()}>Add Retina Image</button>

            {/* Retina Images Table */}
            <table className="products-table">
                <thead>
                    <tr>
                        <th>Name</th>
                        <th>Details</th>
                        <th>Instagram</th>
                        <th>Images</th>
                        <th>Actions</th>
                    </tr>
                </thead>
                <tbody>
                    {retinaImages.map((image) => (
                        <tr key={image.id}>
                            <td>{image.name}</td>
                            <td>{image.details}</td>
                            <td>{image.instagram}</td>
                            <td>
                                {image.imgUrl && image.imgUrl.length > 0 && (
                                    <img 
                                        src={image.imgUrl[0]} 
                                        alt={image.name}
                                        style={{ width: '50px', height: '50px', objectFit: 'cover' }}
                                    />
                                )}
                            </td>
                            <td>
                                <button onClick={() => openModal(image)}>Update</button>
                                <button onClick={() => deleteRetinaImage(image.id)}>Delete</button>
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>

            {/* Modal for Add/Edit */}
            {showModal && (
                <div className="modal">
                    <div className="modal-cont">
                        <h2>{editImageId ? 'Edit Retina Image' : 'Add Retina Image'}</h2>
                        <form onSubmit={(e) => {
                            e.preventDefault();
                            if (editImageId) {
                                editRetinaImage();
                            } else {
                                addRetinaImage();
                            }
                        }}>
                            <div className="form-group">
                                <label>Name</label>
                                <input
                                    type="text"
                                    value={retinaData.name}
                                    onChange={(e) => setRetinaData({ ...retinaData, name: e.target.value })}
                                    required
                                />
                            </div>
                            <div className="form-group">
                                <label>Details</label>
                                <input
                                    type="text"
                                    value={retinaData.details}
                                    onChange={(e) => setRetinaData({ ...retinaData, details: e.target.value })}
                                />
                            </div>
                            <div className="form-group">
                                <label>Instagram URL</label>
                                <input
                                    type="text"
                                    value={retinaData.instagram}
                                    onChange={(e) => setRetinaData({ ...retinaData, instagram: e.target.value })}
                                />
                            </div>

                            <div className="form-group">
                                <label>Images</label>
                                <input
                                    type="file"
                                    accept="image/*"
                                    multiple
                                    onChange={handleFileSelect}
                                />
                                {uploading && <p>Uploading images...</p>}

                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px', marginTop: '10px' }}>
                                    {Array.isArray(retinaData.imgUrl) && retinaData.imgUrl.map((img, index) => (
                                        <img
                                            key={index}
                                            src={img}
                                            onContextMenu={(e) => {
                                                e.preventDefault();
                                                setContextMenu({
                                                    visible: true,
                                                    x: e.pageX,
                                                    y: e.pageY,
                                                    index,
                                                    type: 'main'
                                                });
                                            }}
                                            alt={`Retina-${index}`}
                                            loading="lazy"
                                            style={{ width: '80px', cursor: 'pointer' }}
                                            title="Right click for options"
                                        />
                                    ))}
                                </div>
                            </div>

                            {contextMenu.visible && (
                                <div
                                    className="context-menu"
                                    style={{ top: contextMenu.y, left: contextMenu.x }}
                                    onMouseLeave={() => setContextMenu({ ...contextMenu, visible: false })}
                                >
                                    <div
                                        className="context-menu-option"
                                        onClick={() => {
                                            setImageAsMain(contextMenu.index);
                                            setContextMenu({ ...contextMenu, visible: false });
                                        }}
                                    >
                                        Set as display image
                                    </div>
                                    <div
                                        className="context-menu-option"
                                        onClick={() => {
                                            handleMainImageDelete(contextMenu.index);
                                            setContextMenu({ ...contextMenu, visible: false });
                                        }}
                                    >
                                        Delete
                                    </div>
                                </div>
                            )}

                            <button type="submit">{editImageId ? 'Update Image' : 'Add Image'}</button>
                            <button type="button" onClick={() => setShowModal(false)} className='btn-cancel'>Cancel</button>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ManageRetinaData;